/**
 * Renders a stored AST back into simple-editor rows, and decides whether the
 * simple editor can represent it at all.
 *
 * This is a structural walk, not a parse. The AST is already on the rule, so the
 * website needs no parser to read a rule back — which is what lets the simple and
 * formula modes edit one expression rather than two stored formats.
 */
import { fromMillis } from './expressionBuilder';

/**
 * True when the simple editor can draw this rule faithfully.
 *
 * Representable: a band node, a single comparison, or a FLAT all-and / all-or
 * chain of comparisons. Anything else opens in formula mode with the reason
 * shown — deliberately, rather than drawing a subset of the rule and silently
 * dropping the rest when the user saves.
 */
export const isSimpleRepresentable = (ast) => {
  const body = ast?.body;
  if (!body) return false;
  if (body.type === 'band') return body.bands?.length >= 2 && isSimpleTerm(body.term);
  if (body.type === 'comparison') return isSimpleTerm(body.term);
  if (body.type === 'and' || body.type === 'or') {
    return (body.operands || []).length >= 2
      && body.operands.every((o) => o.type === 'comparison' && isSimpleTerm(o.term));
  }
  return false;
};

/** Why the simple editor cannot show this rule, in words a user can act on. */
export const nonRepresentableReason = (ast) => {
  if (isSimpleRepresentable(ast)) return null;
  const body = ast?.body;
  if (!body) return 'This rule has not been validated yet.';
  if (body.type === 'and' || body.type === 'or') {
    return 'This rule nests conditions in a way the simple editor cannot draw.';
  }
  return 'This rule uses a form the simple editor cannot draw.';
};

const isSimpleTerm = (term) =>
  !!term && ['channel', 'aggregation', 'aqi'].includes(term.type);

const termToRow = (term) => {
  if (term.type === 'channel') {
    return { channel: term.groupKey, aggregation: 'threshold', window: { amount: 10, unit: 'm' } };
  }
  const aggregation = term.type === 'aqi' ? 'aqi' : term.fn;
  return {
    channel: term.channel.groupKey,
    aggregation,
    window: fromMillis(term.windowMs),
  };
};

/** Turns an AST into the simple editor's state. Returns null when not representable. */
export const astToRows = (ast) => {
  if (!isSimpleRepresentable(ast)) return null;
  const { body } = ast;
  const trailing = {
    sustain: ast.sustainMs > 0 ? fromMillis(ast.sustainMs) : null,
    settle: ast.settleMs !== 5000 ? fromMillis(ast.settleMs) : null,
  };

  if (body.type === 'band') {
    return {
      mode: 'bands',
      term: termToRow(body.term),
      rows: body.bands.map((b) =>
        b.catchAll
          ? { label: b.label, comparator: '<', value: '', catchAll: true }
          : { label: b.label, comparator: b.comparator, value: String(b.value) }),
      ...trailing,
    };
  }

  const comparisons = body.type === 'comparison' ? [body] : body.operands;
  return {
    mode: 'conditions',
    joiner: body.type === 'or' ? 'or' : 'and',
    rows: comparisons.map((c) => ({
      ...termToRow(c.term),
      comparator: c.comparator,
      value: String(c.value),
    })),
    ...trailing,
  };
};
